/**
 * Savings other than the US accounts on the transfer tab, in rupees at today's
 * value and before tax. Kept in this browser only, never in the share link.
 */
export interface OtherSavings {
  indianMarket: number;
  deposits: number;
  providentFunds: number;
  nps: number;
  abroad: number;
  other: number;
}

export const EMPTY_OTHER_SAVINGS: OtherSavings = {
  indianMarket: 0,
  deposits: 0,
  providentFunds: 0,
  nps: 0,
  abroad: 0,
  other: 0,
};

/** NPS can't be fully withdrawn before 60, so it only counts from that age. */
export const NPS_UNLOCK_AGE = 60;

const STORAGE_KEY = "fatfire-other-savings-v1";

export function loadOtherSavings(): OtherSavings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_OTHER_SAVINGS;
    const parsed = JSON.parse(raw) as Partial<OtherSavings>;
    const out = { ...EMPTY_OTHER_SAVINGS };
    for (const k of Object.keys(out) as (keyof OtherSavings)[]) {
      const v = Number(parsed[k]);
      out[k] = Number.isFinite(v) && v > 0 ? v : 0;
    }
    return out;
  } catch {
    return EMPTY_OTHER_SAVINGS;
  }
}

export function saveOtherSavings(s: OtherSavings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    /* storage unavailable: still works for this visit */
  }
}

export interface OtherSavingsTotal {
  /** Counted towards the corpus. */
  counted: number;
  /** NPS balance, and whether it is counted at this retirement age. */
  nps: number;
  npsCounted: boolean;
}

export function totalOtherSavings(s: OtherSavings, ageAtRetirement: number): OtherSavingsTotal {
  const base = s.indianMarket + s.deposits + s.providentFunds + s.abroad + s.other;
  const npsCounted = ageAtRetirement >= NPS_UNLOCK_AGE;
  return { counted: base + (npsCounted ? s.nps : 0), nps: s.nps, npsCounted };
}
